import { reconcileAuthAttribution } from "./authFirstTouch.js";
import { getDormantFeatures, isDormantProductPath } from "./dormantFeatures.js";
import { normalizeStory } from "./marketingAttribution.js";

export const INCOMPLETE_SURVEY_PATH = "/survey";
export const TRIPS_HOME_PATH = "/trips";

const AUTH_NEXT_KEY = "dople_auth_next_path";
const AUTH_NEXT_TTL_MS = 60 * 60 * 1000;
const MAX_NEXT_LENGTH = 512;
const OAUTH_NEXT_KEY = "dople_auth_next";
const OAUTH_ATTRIBUTION_KEY = "dople_auth_attribution";

// Same keys landingQuery already copies across redirects, plus cta_source.
// step stays on a /survey next only. next, plan, and other params are not copied.
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"];
const UTM_KEY = /^utm_[a-z][a-z0-9_]{0,31}$/;
const CTA_SOURCE_VALUE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const TOKEN_VALUE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const STEP_VALUE = /^[A-Za-z0-9_-]{1,32}$/;
const CLICK_VALUE = /^[A-Za-z0-9._~+/-]{1,255}$/;
const UTM_VALUE = /^[A-Za-z0-9 ._~+/-]{1,100}$/;

function hasControlCharacter(value) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 32 || code === 127) return true;
  }
  return false;
}

function unsafeInternalPath(value) {
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return true;
  if (value.includes("\\")) return true;
  if (hasControlCharacter(value)) return true;
  const path = value.split("?")[0].split("#")[0];
  if (path.toLowerCase().includes("://")) return true;
  return false;
}

function blockedAuthPath(value) {
  const path = value.split("?")[0].split("#")[0].toLowerCase();
  return path === "/login" || path.startsWith("/login/") || path === "/signup" || path.startsWith("/signup/");
}

// Same-origin relative paths only. One extra decode catches a hidden "//".
export function sanitizeInternalPath(value) {
  const next = String(value || "").trim();
  if (!next || next.length > MAX_NEXT_LENGTH) return "";
  let decoded = next;
  try {
    decoded = decodeURIComponent(next);
  } catch {
    // A malformed percent sign is still the original path. Do not drop it.
    decoded = next;
  }
  if (!decoded || decoded.length > MAX_NEXT_LENGTH) return "";
  if (unsafeInternalPath(next) || unsafeInternalPath(decoded) || blockedAuthPath(decoded)) return "";
  // Validate the decoded form but keep the original so encoded query values stay intact.
  return next;
}

// URLSearchParams decodes the query once. Do not decode it again here.
export function readSameOriginNext(search) {
  let next = "";
  try {
    next = new URLSearchParams(String(search || "")).get("next") || "";
  } catch {
    return "";
  }
  return sanitizeInternalPath(next);
}

function signedOutEntry(entry, pathname, search = "", hash = "") {
  const path = String(pathname || "").trim() || "/";
  const query = String(search || "");
  const suffix = query ? (query.startsWith("?") ? query : `?${query}`) : "";
  const fragment = String(hash || "");
  const hashSuffix = fragment ? (fragment.startsWith("#") ? fragment : `#${fragment}`) : "";
  const safe = sanitizeInternalPath(`${path}${suffix}${hashSuffix}`);
  if (!safe) return entry;
  return `${entry}?next=${encodeURIComponent(safe)}`;
}

export function loginRedirectForSignedOut(pathname, search = "", hash = "") {
  return signedOutEntry("/login", pathname, search, hash);
}

// A signed-out /survey visit is a new visitor, so it goes to signup.
export function signupRedirectForSignedOut(pathname, search = "", hash = "") {
  return signedOutEntry("/signup", pathname, search, hash);
}

// Signed-out /survey goes to signup. Every other guarded path keeps login.
export function signedOutRedirectFor(pathname, search = "", hash = "") {
  return String(pathname || "") === INCOMPLETE_SURVEY_PATH
    ? signupRedirectForSignedOut(pathname, search, hash)
    : loginRedirectForSignedOut(pathname, search, hash);
}

export function rememberAuthNext(value) {
  const safe = sanitizeInternalPath(value);
  try {
    if (!safe) {
      window.localStorage.removeItem(AUTH_NEXT_KEY);
      return "";
    }
    window.localStorage.setItem(AUTH_NEXT_KEY, JSON.stringify({ path: safe, storedAt: Date.now() }));
  } catch {
    // The query string still carries the path when storage is blocked.
  }
  return safe;
}

export function readAuthNext() {
  try {
    const raw = window.localStorage.getItem(AUTH_NEXT_KEY) || "";
    if (!raw) return "";
    let path = raw;
    let storedAt = 0;
    if (raw.startsWith("{")) {
      const parsed = JSON.parse(raw);
      path = String(parsed?.path || "");
      storedAt = Number(parsed?.storedAt) || 0;
    }
    if (!storedAt || Date.now() - storedAt > AUTH_NEXT_TTL_MS) {
      window.localStorage.removeItem(AUTH_NEXT_KEY);
      return "";
    }
    const safe = sanitizeInternalPath(path);
    if (!safe) {
      window.localStorage.removeItem(AUTH_NEXT_KEY);
      return "";
    }
    return safe;
  } catch {
    return "";
  }
}

export function clearAuthNext() {
  try {
    window.localStorage.removeItem(AUTH_NEXT_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

export function isHiddenProductPath(value, features = getDormantFeatures()) {
  const next = sanitizeInternalPath(value);
  if (!next) return false;
  return isDormantProductPath(next, features);
}

// Flags off: dashboard, chat, and money bookmarks land on Trips.
// Flags on: those paths stay, which is the original post-auth behavior.
export function tripsOnlyPath(value, features = getDormantFeatures()) {
  const next = sanitizeInternalPath(value);
  if (!next) return "";
  if (isHiddenProductPath(next, features)) return TRIPS_HOME_PATH;
  return next;
}

function attributionKey(key) {
  const normalized = String(key || "").trim().toLowerCase();
  if (
    normalized === "cta_source"
    || normalized === "story"
    || normalized === "ref"
    || CLICK_KEYS.includes(normalized)
  ) return normalized;
  if (UTM_KEY.test(normalized)) return normalized;
  return "";
}

// Allowlisted tokens only. A value that is a URL, a path, or an address is
// dropped so the survey query cannot become an open redirect.
function safeQueryValue(key, value) {
  const raw = String(value || "").trim();
  if (!raw || hasControlCharacter(raw)) return "";
  if (raw.includes("\\") || raw.includes("://") || raw.startsWith("//") || raw.startsWith("/")) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.includes("@")) return "";
  if (key === "cta_source") {
    const token = raw.toLowerCase();
    return CTA_SOURCE_VALUE.test(token) ? token : "";
  }
  if (key === "step") return STEP_VALUE.test(raw) ? raw : "";
  if (key === "ref") return TOKEN_VALUE.test(raw) ? raw : "";
  if (key === "story") return normalizeStory(raw);
  if (CLICK_KEYS.includes(key)) return CLICK_VALUE.test(raw) ? raw : "";
  if (UTM_KEY.test(key)) return UTM_VALUE.test(raw) ? raw : "";
  return "";
}

function firstParam(params, key) {
  let found = "";
  params.forEach((value, rawKey) => {
    if (found) return;
    if (String(rawKey || "").trim().toLowerCase() === key) found = value;
  });
  return found;
}

function carryParam(params, kept, key) {
  if (kept.has(key)) return;
  const safeValue = safeQueryValue(key, firstParam(params, key));
  if (safeValue) kept.set(key, safeValue);
}

function carryUtm(params, kept) {
  params.forEach((value, rawKey) => {
    const key = String(rawKey || "").trim().toLowerCase();
    if (!UTM_KEY.test(key) || kept.has(key)) return;
    const safeValue = safeQueryValue(key, value);
    if (safeValue) kept.set(key, safeValue);
  });
}

function carryAttribution(params, kept) {
  carryParam(params, kept, "cta_source");
  carryParam(params, kept, "story");
  CLICK_KEYS.forEach((key) => carryParam(params, kept, key));
  carryUtm(params, kept);
}

function splitPathQuery(value) {
  const hashAt = value.indexOf("#");
  const withoutHash = hashAt === -1 ? value : value.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : value.slice(hashAt);
  const queryAt = withoutHash.indexOf("?");
  const pathname = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const query = queryAt === -1 ? "" : withoutHash.slice(queryAt + 1);
  return { pathname, query, hash };
}

// Incomplete strangers always land on the survey. A same-origin /survey next
// keeps ref and step. cta_source, story, click ids, and utm_* ride along from
// that next, including when the next itself is not /survey. Every other next,
// including an off-site URL, is dropped so a post-auth hint cannot leave them
// on trips. Query values are allowlisted and cannot be a URL.
export function incompleteSurveyPath(requestedPath) {
  const safe = sanitizeInternalPath(requestedPath);
  if (!safe) return INCOMPLETE_SURVEY_PATH;
  const { pathname, query } = splitPathQuery(safe);
  const params = new URLSearchParams(query);
  const kept = new URLSearchParams();
  if (pathname === INCOMPLETE_SURVEY_PATH) {
    carryParam(params, kept, "ref");
    carryParam(params, kept, "step");
  }
  carryAttribution(params, kept);
  const nextQuery = kept.toString();
  return nextQuery ? `${INCOMPLETE_SURVEY_PATH}?${nextQuery}` : INCOMPLETE_SURVEY_PATH;
}

// Copy allowlisted attribution from the page the person is leaving onto an
// already safe internal path. next is not copied. Existing query keys win.
export function mergeAttributionOntoPath(path, search = "") {
  const safe = sanitizeInternalPath(path);
  if (!safe) return "";
  const { pathname, query, hash } = splitPathQuery(safe);
  const params = new URLSearchParams(query);
  let incoming;
  try {
    incoming = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  } catch {
    incoming = new URLSearchParams();
  }
  incoming.forEach((value, rawKey) => {
    const key = attributionKey(rawKey);
    if (!key || params.has(key)) return;
    const safeValue = safeQueryValue(key, value);
    if (!safeValue) return;
    params.append(key, safeValue);
  });
  const nextQuery = params.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hash}`;
}

function forEachAttributionKey(search, visit) {
  let incoming;
  try {
    incoming = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  } catch {
    return;
  }
  const seen = new Set();
  incoming.forEach((value, rawKey) => {
    const key = attributionKey(rawKey);
    if (!key || key === "ref" || seen.has(key)) return;
    seen.add(key);
    visit(key);
  });
}

// Replace login values with the stored first touch. A key the first touch
// does not have yet keeps the login value. ref stays on the path as-is.
function overlayFirstTouch(path, search, merged) {
  const { pathname, query, hash } = splitPathQuery(path);
  const params = new URLSearchParams(query);
  const apply = (key) => {
    const safeValue = safeQueryValue(key, merged[key]);
    if (!safeValue) return;
    if (params.has(key)) params.set(key, safeValue);
    else params.append(key, safeValue);
  };
  const seen = new Set();
  [String(search || "").replace(/^\?/, ""), query].forEach((source) => {
    forEachAttributionKey(source, (key) => {
      if (seen.has(key)) return;
      seen.add(key);
      apply(key);
    });
  });
  const nextQuery = params.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hash}`;
}

function firstTouchQuery(search, merged) {
  const kept = new URLSearchParams();
  forEachAttributionKey(search, (key) => {
    if (kept.has(key)) return;
    const safeValue = safeQueryValue(key, merged[key]);
    if (safeValue) kept.set(key, safeValue);
  });
  return kept.toString();
}

// Page search and the safe path query are one login touch. Stored first-touch
// keys win. Login fills only the keys that touch did not already have.
export function carryAuthAttribution(path, search = "") {
  const base = mergeAttributionOntoPath(path, search);
  if (!base) return "";
  const { query } = splitPathQuery(base);
  const page = String(search || "").replace(/^\?/, "");
  const combined = [page, query].filter(Boolean).join("&");
  const merged = reconcileAuthAttribution(combined);
  return overlayFirstTouch(base, page, merged);
}

function browserSession() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// Google and Apple leave the auth page before the callback. The callback
// search is the provider code, so the attribution query is stored here.
export function rememberAuthAttribution(search) {
  const storage = browserSession();
  const query = (() => {
    const kept = new URLSearchParams();
    let incoming;
    try {
      incoming = new URLSearchParams(String(search || "").replace(/^\?/, ""));
    } catch {
      return "";
    }
    incoming.forEach((value, rawKey) => {
      const key = attributionKey(rawKey);
      if (!key || kept.has(key)) return;
      const safeValue = safeQueryValue(key, value);
      if (safeValue) kept.set(key, safeValue);
    });
    return kept.toString();
  })();
  if (!storage) return query;
  try {
    if (!query) storage.removeItem(OAUTH_ATTRIBUTION_KEY);
    else storage.setItem(OAUTH_ATTRIBUTION_KEY, query);
  } catch {
    // The stored next still carries the survey query when storage is blocked.
  }
  return query;
}

export function takeAuthAttribution() {
  const storage = browserSession();
  if (!storage) return "";
  try {
    const raw = storage.getItem(OAUTH_ATTRIBUTION_KEY) || "";
    storage.removeItem(OAUTH_ATTRIBUTION_KEY);
    return raw;
  } catch {
    return "";
  }
}

// Same handoff for Google-first signup and the other provider buttons.
export function rememberOAuthHandoff(nextPath, search = "") {
  const stored = nextPath ? carryAuthAttribution(nextPath, search) : "";
  const attributionSearch = stored
    ? splitPathQuery(stored).query
    : firstTouchQuery(search, reconcileAuthAttribution(search));
  rememberAuthAttribution(attributionSearch);
  const storage = browserSession();
  if (!storage) return stored;
  try {
    if (stored) storage.setItem(OAUTH_NEXT_KEY, stored);
  } catch {
    // takeAuthAttribution still has the query for the callback.
  }
  return stored;
}

export function decideNextPath(status, features = getDormantFeatures()) {
  if (!status?.surveyCompleted) return INCOMPLETE_SURVEY_PATH;
  const next = tripsOnlyPath(status?.nextPath, features) || TRIPS_HOME_PATH;
  // Completed members opened the dashboard. The access API sends /trips while
  // the surface is dormant, so the client restores that landing when the flag is on.
  if (features.dashboard && status?.appAccessGranted && next === TRIPS_HOME_PATH) return "/dashboard";
  return next;
}

export function resolvePostAuthDestination(status, requestedPath = "", features = getDormantFeatures()) {
  if (!status?.surveyCompleted) return incompleteSurveyPath(requestedPath);
  const requiredPath = decideNextPath(status, features);
  return tripsOnlyPath(requestedPath, features) || requiredPath;
}

// A session that opens /r/<code> hard-lands on /survey. FlowGuard sends a
// finished survey on to the real next path. Logged-out visits keep the
// public home. This does not create a trip and does not skip the survey.
export function affiliateShortLinkDestination({ isAuthenticated = null, hasAccessToken = false } = {}) {
  if (isAuthenticated === true || hasAccessToken) return INCOMPLETE_SURVEY_PATH;
  return "/";
}

// Trip-only buyers (no membership) resolve to /trips. They may still open
// /connect for the Connect step before a paid trip's itinerary. This does not
// open Dashboard, Chat, or money pages.
export function hasTripConnectResume(search = "", pendingTripId = "") {
  try {
    const params = new URLSearchParams(search || "");
    if (params.get("resume") === "itinerary" && String(params.get("trip_id") || "").trim()) {
      return true;
    }
  } catch {
    // Fall through to the stored handoff.
  }
  return Boolean(String(pendingTripId || "").trim());
}

export function isAllowedWithinRequiredPath(currentPath, requiredPath, { tripConnectResume = false } = {}) {
  if (currentPath === requiredPath) return true;
  if (requiredPath === "/trips") {
    if (currentPath === "/connect") return Boolean(tripConnectResume);
    return currentPath === "/trips/create"
      || currentPath === "/trips/building"
      || currentPath.startsWith("/trips/");
  }
  return false;
}
